import React from "react";
import { ConnectionFlight, FlightSegment } from "../types";
import { formatDateTime } from "utils/dateUtils";
import { getAirportOffsetLabel } from "utils/airportTimezones";
import { getConnectionTotalDuration } from "./loadsUtils";
import DefaultAirLogo from "../../../assets/images/logo.svg";

interface ConnectionCardProps {
  connection: ConnectionFlight;
  index: number;
  readOnly?: boolean;
  isSegmentSelected: (segment: FlightSegment, connectionId: string) => boolean;
  areAllSegmentsSelected: (connection: ConnectionFlight) => boolean;
  toggleSegmentSelection: (
    segment: FlightSegment,
    connectionId: string,
    event?: React.MouseEvent,
  ) => void;
  toggleAllSegmentsInConnection: (connection: ConnectionFlight) => void;
}

const layoverText = (minutes: number, suffix = "") => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m layover${suffix}` : `${m}m layover${suffix}`;
};

// Same status→colour mapping the direct FlightCard uses, applied per segment.
const statusColorFor = (segment: FlightSegment) => {
  const s = segment.liveStatus;
  if (s === "Landed" || s === "Arrived")
    return { bg: "bg-green-500", text: "text-white" };
  if (s === "Cancelled") return { bg: "bg-red-500", text: "text-white" };
  if (s === "Early") return { bg: "bg-blue-500", text: "text-white" };
  if (segment.delayMinutes && segment.delayMinutes > 0)
    return { bg: "bg-orange-500", text: "text-white" };
  return { bg: "bg-blue-500", text: "text-white" };
};

const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  index,
  readOnly = false,
  isSegmentSelected,
  areAllSegmentsSelected,
  toggleSegmentSelection,
  toggleAllSegmentsInConnection,
}) => {
  const connectionCompositeId =
    connection.compositeId ||
    `${connection.segments[0]?.id}_${connection.overallDepartureTime}`;
  const allSegmentsSelected = areAllSegmentsSelected(connection);

  // Layover between two consecutive segments. Both times are local to the same
  // (connection) airport, so a direct diff is correct regardless of time zone.
  const renderLayover = (
    fromSeg: FlightSegment,
    toSeg: FlightSegment,
    key: string,
  ) => {
    const minutes = Math.max(
      0,
      Math.round(
        (new Date(toSeg.departureTime).getTime() -
          new Date(fromSeg.arrivalTime).getTime()) /
          60000,
      ),
    );
    return (
      <div key={key} className="flex items-center justify-center my-2">
        <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 rounded-full">
          <i className="st-clock text-yellow-600 text-xs"></i>
          <p className="text-xs text-yellow-700 font-medium">
            {layoverText(minutes, ` in ${fromSeg.arrivalCode}`)}
          </p>
        </div>
      </div>
    );
  };

  const renderSegment = (segment: FlightSegment, segmentIndex: number) => {
    const isSegSelected = isSegmentSelected(segment, connectionCompositeId);
    const statusColors = statusColorFor(segment);

    // Shared values, used by both the desktop (3-column) and mobile (stacked)
    // layouts of the flight-details box below.
    const timeOpts: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const depCity = segment.departureAirportName?.split(" ")[0];
    const arrCity = segment.arrivalAirportName?.split(" ")[0];
    const depTime = formatDateTime(segment.departureTime, timeOpts);
    const arrTime = formatDateTime(segment.arrivalTime, timeOpts);
    const depOffset = getAirportOffsetLabel(
      segment.departureCode,
      segment.departureTime,
    );
    const arrOffset = getAirportOffsetLabel(
      segment.arrivalCode,
      segment.arrivalTime,
    );
    const durationText = segment.duration
      ? `${parseInt(segment.duration.slice(0, 2), 10)}h ${segment.duration.slice(3, 5)}m`
      : "";
    const flightLine = (
      <div className="flex items-center w-full">
        <div className="w-2 h-2 bg-blue-100 rounded-full flex-shrink-0"></div>
        <div className="flex-1 border-b border-dashed border-indigo-200 mx-1"></div>
        <i className="st-flight-slim text-royalblue-100 text-xs mx-1" />
        <div className="flex-1 border-b border-dashed border-indigo-200 mx-1"></div>
        <div className="w-2 h-2 bg-blue-100 rounded-full flex-shrink-0"></div>
      </div>
    );

    return (
      <div
        key={segment.compositeId || `segment-${segment.id}-${segmentIndex}`}
        className={`relative rounded-lg p-3 mb-3 border transition-all ${
          readOnly ? "" : "cursor-pointer"
        } ${
          !readOnly && isSegSelected
            ? "border-[#FFD700] bg-yellow-50"
            : "border-gray-200 bg-white" + (readOnly ? "" : " hover:bg-gray-50")
        }`}
        onClick={(e) => {
          if (readOnly) return;
          toggleSegmentSelection(segment, connectionCompositeId, e);
        }}
      >
        {/* Live status pill (per segment) */}
        {segment.liveStatus && (
          <span
            className={`absolute -top-2.5 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium shadow-sm ${statusColors.bg} ${statusColors.text}`}
          >
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            {segment.liveStatus}
            {segment.delayMinutes != null && segment.delayMinutes !== 0 && (
              <span className="ml-0.5">
                {segment.delayMinutes > 0
                  ? `+${segment.delayMinutes}m`
                  : `${segment.delayMinutes}m`}
              </span>
            )}
          </span>
        )}

        {/* Segment Header with Checkbox */}
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-3">
            {!readOnly && (
              <input
                type="checkbox"
                checked={isSegSelected}
                // Stop the click from also bubbling to the card's onClick,
                // which would toggle this segment a second time.
                onClick={(e) => e.stopPropagation()}
                onChange={() =>
                  toggleSegmentSelection(segment, connectionCompositeId)
                }
                className="w-4 h-4 text-[#FFD700] border-gray-300 rounded focus:ring-[#FFD700] cursor-pointer"
              />
            )}
            <img
              className="w-10 h-10 object-contain rounded"
              src={segment.airlineLogo || DefaultAirLogo}
              alt={segment.airlineName}
              onError={(e) => {
                e.currentTarget.src = DefaultAirLogo;
              }}
              loading="lazy"
            />
            <div>
              <p className="text-lightgray-200 text-xs">Airlines</p>
              <p className="text-grease-100 mt-1 text-xs font-medium">
                {segment.airlineName}
              </p>
            </div>
          </div>
          <div>
            <p className="text-lightgray-200 text-xs">Flight No.</p>
            <p className="text-grease-100 mt-1 text-xs font-medium">
              {segment.flightNumber}
            </p>
          </div>
        </div>

        {/* Segment Flight Details */}
        <div className="bg-lightblue-100 p-3 rounded-lg">
          {/* Desktop: departure | flight-line | arrival in one row. */}
          <div className="hidden md:block">
            <div className="flex items-center gap-4">
            <div className="flex flex-col items-start gap-1 min-w-[80px]">
              <p className="text-grease-100 text-xs font-medium">
                {segment.departureCode}
              </p>
              <p className="text-lightgray-200 text-[10px]">{depCity}</p>
              <p className="text-lightgray-200 text-[10px]">
                {depTime}
                {depOffset && (
                  <span className="ml-1 text-lightgray-150">({depOffset})</span>
                )}
              </p>
            </div>

            <div className="flex flex-col items-center flex-1 min-w-0">
              {flightLine}
              <p className="text-lightgray-150 text-[10px] text-center mt-1">
                {durationText}
              </p>
            </div>

            <div className="flex flex-col items-start gap-1 min-w-[80px]">
              <p className="text-grease-100 text-xs font-medium">
                {segment.arrivalCode}
              </p>
              <p className="text-lightgray-200 text-[10px]">{arrCity}</p>
              <p className="text-lightgray-200 text-[10px]">
                {arrTime}
                {arrOffset && (
                  <span className="ml-1 text-lightgray-150">({arrOffset})</span>
                )}
              </p>
            </div>
            </div>
          </div>

          {/* Mobile: a compact route row (codes + line + duration), then the
              city/time/offset details in a two-column row below with room. */}
          <div className="md:hidden">
            <div className="flex items-center gap-2">
              <p className="text-grease-100 text-sm font-semibold shrink-0">
                {segment.departureCode}
              </p>
              <div className="flex flex-col items-center flex-1 min-w-0">
                {flightLine}
                <p className="text-lightgray-150 text-[10px] text-center mt-0.5">
                  {durationText}
                </p>
              </div>
              <p className="text-grease-100 text-sm font-semibold shrink-0">
                {segment.arrivalCode}
              </p>
            </div>
            <div className="flex justify-between gap-3 mt-2">
              <div className="min-w-0">
                <p className="text-lightgray-200 text-[10px] truncate">
                  {depCity}
                </p>
                <p className="text-lightgray-200 text-[10px]">{depTime}</p>
                {depOffset && (
                  <p className="text-lightgray-150 text-[10px]">({depOffset})</p>
                )}
              </div>
              <div className="min-w-0 text-right">
                <p className="text-lightgray-200 text-[10px] truncate">
                  {arrCity}
                </p>
                <p className="text-lightgray-200 text-[10px]">{arrTime}</p>
                {arrOffset && (
                  <p className="text-lightgray-150 text-[10px]">({arrOffset})</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      key={
        connectionCompositeId ||
        `connection-${connection.segments[0].id}-${index}`
      }
      className={`bg-white rounded-lg p-5 border ${
        readOnly ? "" : "cursor-pointer"
      } ${
        !readOnly && allSegmentsSelected
          ? "border-[#FFD700] border-xl shadow-lg"
          : "border-gray-200"
      }`}
      onClick={() => {
        if (readOnly) return;
        toggleAllSegmentsInConnection(connection);
      }}
    >
      {/* Header with via airport and Select All checkbox */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1">
          {!readOnly && (
            <input
              type="checkbox"
              checked={allSegmentsSelected}
              // Stop the click from also bubbling to the card's onClick.
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggleAllSegmentsInConnection(connection)}
              className="w-4 h-4 text-[#FFD700] border-gray-300 rounded focus:ring-[#FFD700] cursor-pointer flex-shrink-0 mt-1"
              title="Select all segments"
            />
          )}
          <div className="flex-1">
            <p className="text-sm text-grease-100 font-medium">
              via{" "}
              {connection.connectionAirportName || connection.connectionAirport}
            </p>
            <p className="text-xs text-lightgray-200 mt-1">
              {layoverText(connection.layoverMinutes)}
            </p>
          </div>
        </div>
      </div>

      {/* All segments, with a layover indicator between each consecutive pair.
          Handles any number of stops (2 segments for 1 stop, 3 for 2 stops…). */}
      {connection.segments.map((segment, i) => (
        <React.Fragment
          key={segment.compositeId || `seg-${segment.id}-${i}`}
        >
          {renderSegment(segment, i)}
          {i < connection.segments.length - 1 &&
            renderLayover(
              segment,
              connection.segments[i + 1],
              `layover-${segment.id}-${i}`,
            )}
        </React.Fragment>
      ))}

      {/* Total Duration */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-xs text-grease-100 text-center">
          Total duration: {getConnectionTotalDuration(connection)}
        </p>
      </div>
    </div>
  );
};

export default ConnectionCard;
